import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import VehicleMonthlyCostsService, { RecalculationStatus } from 'api/vehicleMonthlyCosts/VehicleMonthlyCostsService';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { Reducers } from 'store/types';

const CalculateAllMonthlyCostsScreen: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [status, setStatus] = useState<RecalculationStatus | null>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const startedRef = useRef(false);

    const startRecalculation = async () => {
        if (!UsersService.isPlatformAdmin(userProfile?.roles) || startedRef.current) return;
        startedRef.current = true;

        try {
            await VehicleMonthlyCostsService.calculateAllMonthlyCosts();
            startPolling();
        } catch (error: any) {
            if (error?.response?.status === 409) {
                startPolling();
            } else {
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            }
        }
    };

    const startPolling = () => {
        if (pollingRef.current) return;
        pollStatus();
        pollingRef.current = setInterval(pollStatus, 5000);
    };

    const pollStatus = async () => {
        try {
            const result = await VehicleMonthlyCostsService.getAllRecalculationStatus();
            setStatus(result);

            if (!result.isRunning) {
                stopPolling();
                if (result.error) {
                    addToast(result.error, { appearance: 'error' });
                } else {
                    addToast(t('common.messages.record_save_success'), { appearance: 'success' });
                }
            }
        } catch {
            stopPolling();
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    useEffect(() => {
        startRecalculation();
        return () => stopPolling();
    }, []);

    const progressPercent = status && status.totalVehicles > 0
        ? Math.round((status.processedVehicles / status.totalVehicles) * 100)
        : 0;

    return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
            {status?.isRunning && (
                <>
                    <h3>{t('vehicle.recalculating_monthly_costs')}</h3>
                    <div style={{ margin: '20px auto', maxWidth: '400px', backgroundColor: '#e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{
                            width: `${progressPercent}%`,
                            height: '30px',
                            backgroundColor: '#02c7c7',
                            transition: 'width 0.5s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            minWidth: progressPercent > 0 ? '40px' : '0',
                        }}>
                            {progressPercent}%
                        </div>
                    </div>
                    <p>{status.processedVehicles} / {status.totalVehicles}</p>
                </>
            )}
            {status && !status.isRunning && !status.error && status.finishedAt && (
                <h3>{t('common.messages.record_save_success')}</h3>
            )}
            {status && !status.isRunning && status.error && (
                <h3 style={{ color: '#e04f5f' }}>{status.error}</h3>
            )}
        </div>
    );
};

export default CalculateAllMonthlyCostsScreen;
